type NumberValue = string | number | null | undefined

// Check if a currency value is negative
export const isCurrencyNegative = (val: NumberValue): boolean => {
  if (val === null || val === undefined || val === "") return false
  const num = parseFloat(String(val))
  return !isNaN(num) && num < 0
}

// Format currency for display (with symbol and commas): $1,234.56 or ($1,234.56)
export const formatCurrencyDisplay = (val: NumberValue, symbol: string = "$"): string => {
  if (val === null || val === undefined || val === "") return ""
  const num = parseFloat(String(val))
  if (isNaN(num)) return ""

  const isNegative = num < 0
  const absFormatted = Math.abs(num).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  return isNegative ? `(${symbol}${absFormatted})` : `${symbol}${absFormatted}`
}

// Format currency for input fields (no commas, no $ sign): 100.00 or (100.00)
export const formatCurrencyInput = (val: NumberValue): string => {
  if (val === null || val === undefined || val === "") return ""
  const num = parseFloat(String(val))
  if (isNaN(num)) return ""

  const isNegative = num < 0
  const absFormatted = Math.abs(num).toFixed(2)

  return isNegative ? `(${absFormatted})` : absFormatted
}

// Parse currency input to storage format: -100.00 or 100.00 or null
export const parseCurrencyInput = (input: string): string | null => {
  if (!input || input.trim() === "") return null

  const isNegative = input.includes("(") || input.includes("-")
  const numericOnly = input.replace(/[^0-9.]/g, "")

  if (!numericOnly) return null

  const num = parseFloat(numericOnly)
  if (isNaN(num)) return null

  const formatted = num.toFixed(2)
  return isNegative ? `-${formatted}` : formatted
}

// Format number with up to 4 decimals, trimming trailing zeros
export const formatNumber = (val: NumberValue): string => {
  if (val === null || val === undefined || val === "") return ""
  const num = parseFloat(String(val))
  if (isNaN(num)) return ""
  const fixed = num.toFixed(4)
  return fixed.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "")
}

// Parser allowing digits, decimal point, and minus sign
export const parseNumber = (val: string): string => val.replace(/[^0-9.-]/g, "")

// Parser for currency input allowing parens, $, minus, digits, decimal
export const parseCurrencyChars = (val: string): string => val.replace(/[^0-9.()$-]/g, "")
